import React, { useState, useEffect, useRef, useCallback } from 'react';
import xService from '../services/xService';
import promptService from '../services/promptService';
import MarkdownRenderer from './MarkdownRenderer';
import { textToHtml } from './SummaryEditor';

/**
 * Dialog component for custom AI analysis of X search results.
 * 
 * Allows users to:
 * - Select a custom prompt with the option to tweak it
 * - Select multiple older results to analyze together
 * - Poll bedrock service until analysis is complete
 */
export default function XCustomAnalysisDialog({ 
    show, 
    onClose, 
    searchId, 
    searchName,
    availableResults = [] // Array of {id, executed_at, tweet_count, run_type?, schedule_id?}
}) {
    const [jobId, setJobId] = useState(null);
    const [status, setStatus] = useState('configuring');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [pollingActive, setPollingActive] = useState(false);
    const analysisStartedRef = useRef(false);
    const pollingIntervalRef = useRef(null);
    const pollingTimeoutRef = useRef(null);

    // Prompt selection state
    const [clientTypePrompts, setClientTypePrompts] = useState([]);
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [selectedPromptId, setSelectedPromptId] = useState('');
    const [systemPrompt, setSystemPrompt] = useState('');
    const [userPrompt, setUserPrompt] = useState('');
    const [customPrompt, setCustomPrompt] = useState('');

    // Result selection state
    const [selectedResultIds, setSelectedResultIds] = useState([]);

    // Load client prompts on mount and select most recent result by default
    useEffect(() => {
        if (show) {
            loadClientTypePrompts();
            // Select the most recent result (first in the array) by default
            if (availableResults && availableResults.length > 0) {
                setSelectedResultIds([availableResults[0].id]);
            }
        }
    }, [show, availableResults]);

    const loadClientTypePrompts = async () => {
        try {
            setPromptsLoading(true);
            const response = await promptService.getPrompts();

            if (response.success && response.prompts) {
                // Filter for X-related prompts (app_id starts with 'X_')
                const xPrompts = response.prompts.filter(prompt => 
                    (prompt.app_id && prompt.app_id.startsWith('X_')) ||
                    (prompt.name && prompt.name.startsWith('X_'))
                );
                setClientTypePrompts(xPrompts);
                console.log('Loaded X prompts:', xPrompts);
            } else {
                console.warn('Failed to load prompts:', response);
                setClientTypePrompts([]);
            }
        } catch (error) {
            console.error('Error loading client type prompts:', error);
            setClientTypePrompts([]);
        } finally {
            setPromptsLoading(false);
        }
    };

    const handlePromptSelection = (promptId) => {
        setSelectedPromptId(promptId);

        if (promptId) {
            const selectedPrompt = clientTypePrompts.find(p => p.prompt_id === promptId);
            if (selectedPrompt) {
                setSystemPrompt(selectedPrompt.system_prompt || '');
                setUserPrompt(selectedPrompt.prompt || '');
            }
        } else {
            setSystemPrompt('');
            setUserPrompt('');
        }
    };

    const toggleResultSelection = (resultId) => {
        setSelectedResultIds(prev => {
            if (prev.includes(resultId)) {
                return prev.filter(id => id !== resultId);
            } else {
                return [...prev, resultId];
            }
        });
    };

    // Stop polling function
    const stopPolling = useCallback(() => {
        console.log('Stopping polling - clearing timers');
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setPollingActive(false);
    }, []);

    // Check status function
    const checkStatus = useCallback(async () => {
        if (!jobId || !searchId) return;

        console.log('Checking custom analysis status...');
        try {
            const response = await xService.getCustomAnalysisStatus(searchId, jobId);

            console.log('Status response:', response.status);
            setStatus(response.status);

            if (response.status === 'completed') {
                console.log('Custom analysis completed! Stopping polling...');
                setResult(response.result);
                stopPolling();
            } else if (response.status === 'failed') {
                console.log('Custom analysis failed! Stopping polling...');
                setError(response.error || 'Analysis failed');
                stopPolling();
            }
        } catch (err) {
            console.error('Error checking custom analysis status:', err);
            // Don't stop polling on transient errors
            if (err.response?.status === 404) {
                console.log('Job not found (404)! Stopping polling...');
                setError('Analysis job not found');
                setStatus('failed');
                stopPolling();
            }
        }
    }, [jobId, searchId, stopPolling]);

    // Poll for status when we have a job ID
    useEffect(() => {
        if (!show || !jobId) return;

        setPollingActive(true);

        // Wait 5 seconds before first check
        pollingTimeoutRef.current = setTimeout(() => {
            checkStatus();
        }, 5000);

        // Then poll every 10 seconds
        pollingIntervalRef.current = setInterval(() => {
            checkStatus();
        }, 10000);

        return () => {
            stopPolling();
        };
    }, [show, jobId, checkStatus, stopPolling]);

    const startCustomAnalysis = async () => {
        try {
            setStatus('starting');
            setError(null);

            // Validate inputs
            if (selectedResultIds.length === 0) {
                setError('Please select at least one result to analyze');
                setStatus('configuring');
                return;
            }

            let customSystemPrompt = null;
            let customUserPrompt = null;

            if (selectedPromptId) {
                // Use selected prompt
                if (!systemPrompt.trim() && !userPrompt.trim()) {
                    setError('Please enter system prompt and/or prompt text');
                    setStatus('configuring');
                    return;
                }
                customSystemPrompt = systemPrompt.trim() || null;
                customUserPrompt = userPrompt.trim() || null;
            } else {
                // Use custom text area
                if (!customPrompt.trim()) {
                    setError('Please enter a custom prompt or select a prompt template');
                    setStatus('configuring');
                    return;
                }
                customUserPrompt = customPrompt.trim();
            }

            const response = await xService.analyzeCustomResults(
                searchId,
                selectedResultIds,
                customSystemPrompt,
                customUserPrompt
            );

            if (response.success && response.job_id) {
                setJobId(response.job_id);
            } else {
                setError('Failed to start custom analysis job');
                setStatus('failed');
            }
        } catch (err) {
            console.error('Error starting custom analysis:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to start analysis');
            setStatus('failed');
        }
    };

    const handleClose = () => {
        stopPolling();
        // Reset state
        setJobId(null);
        setStatus('configuring');
        setResult(null);
        setError(null);
        setSelectedPromptId('');
        setSystemPrompt('');
        setUserPrompt('');
        setCustomPrompt('');
        setSelectedResultIds([]);
        analysisStartedRef.current = false;
        onClose();
    };

    const handleCopyToClipboard = async () => {
        if (!result) return;

        try {
            await navigator.clipboard.writeText(result);
            
            // Show success toast
            const toast = document.createElement('div');
            toast.className = 'alert alert-success position-fixed';
            toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            toast.innerHTML = '<i class="bi bi-check-circle me-2"></i>Copied to clipboard!';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            
            // Show error toast
            const toast = document.createElement('div');
            toast.className = 'alert alert-danger position-fixed';
            toast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            toast.innerHTML = '<i class="bi bi-exclamation-circle me-2"></i>Failed to copy to clipboard';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
        }
    };

    const handleExportToPdf = async () => {
        if (!result) return;

        try {
            // Show loading toast
            const loadingToast = document.createElement('div');
            loadingToast.className = 'alert alert-info position-fixed';
            loadingToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            loadingToast.innerHTML = '<div class="spinner-border spinner-border-sm me-2" role="status"></div>Generating PDF...';
            document.body.appendChild(loadingToast);

            // Helper function to replace emojis with text or remove them
            const cleanTextForPdf = (text) => {
                if (!text) return '';
                
                // Common emoji mappings to text equivalents
                const emojiMap = {
                    '✅': '[CHECK] ',
                    '❌': '[X] ',
                    '⚠️': '[WARNING] ',
                    '⚠': '[WARNING] ',
                    '📊': '[CHART] ',
                    '📈': '[TRENDING UP] ',
                    '📉': '[TRENDING DOWN] ',
                    '💡': '[IDEA] ',
                    '🔥': '[FIRE] ',
                    '👍': '[THUMBS UP] ',
                    '👎': '[THUMBS DOWN] ',
                    '⭐': '[STAR] ',
                    '🎯': '[TARGET] ',
                    '📌': '[PIN] ',
                    '🚀': '[ROCKET] ',
                    '💰': '[MONEY] ',
                    '📱': '[PHONE] ',
                    '💻': '[COMPUTER] ',
                    '🌐': '[GLOBE] ',
                    '📧': '[EMAIL] ',
                    '🔔': '[BELL] ',
                    '⚡': '[LIGHTNING] ',
                    '🎉': '[CELEBRATION] ',
                    '💬': '[COMMENT] ',
                    '📝': '[NOTE] ',
                    '🔍': '[SEARCH] ',
                    '🏆': '[TROPHY] ',
                    '💼': '[BRIEFCASE] ',
                    '📅': '[CALENDAR] ',
                    '⏰': '[ALARM] ',
                    '⏸️': '[PAUSE] ',
                    '⏸': '[PAUSE] ',
                    '▶️': '[PLAY] ',
                    '▶': '[PLAY] ',
                    '⏹️': '[STOP] ',
                    '⏹': '[STOP] ',
                    '⏺️': '[RECORD] ',
                    '⏺': '[RECORD] ',
                    '✨': '[SPARKLES] ',
                    '🎨': '[ART] ',
                    '🔗': '[LINK] ',
                    '📢': '[ANNOUNCEMENT] ',
                    '🌟': '[STAR] ',
                    '💪': '[STRONG] ',
                    '🎓': '[EDUCATION] ',
                    '🔒': '[LOCKED] ',
                    '🔓': '[UNLOCKED] ',
                    '➡️': '-> ',
                    '➡': '-> ',
                    '⬅️': '<- ',
                    '⬅': '<- ',
                    '⬆️': '^ ',
                    '⬆': '^ ',
                    '⬇️': 'v ',
                    '⬇': 'v ',
                    '➕': '+ ',
                    '➖': '- ',
                    '✖️': 'x ',
                    '✖': 'x ',
                    '➗': '/ ',
                };
                
                let cleaned = text;
                
                // Replace known emojis with text equivalents
                Object.entries(emojiMap).forEach(([emoji, replacement]) => {
                    cleaned = cleaned.split(emoji).join(replacement);
                });
                
                // Remove any remaining emojis (Unicode emoji ranges)
                // This regex covers most common emojis
                cleaned = cleaned.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]/gu, '');
                
                return cleaned;
            };

            // Dynamic import of jsPDF
            const { default: jsPDF } = await import('jspdf');

            // Create PDF
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 60;
            const maxWidth = pageWidth - (2 * margin);
            let yPosition = margin;

            // Helper function to add text with word wrapping and page breaks
            const addText = (text, fontSize, isBold = false, color = [0, 0, 0], lineSpacing = 1.4) => {
                pdf.setFontSize(fontSize);
                pdf.setTextColor(...color);

                // Clean text for PDF rendering
                const cleanedText = cleanTextForPdf(text);
                
                // Check if text contains markdown bold **text**
                const hasBoldMarkdown = /\*\*[^*]+\*\*/.test(cleanedText);
                
                if (hasBoldMarkdown && !isBold) {
                    // Process text with inline bold formatting
                    const segments = [];
                    let remaining = cleanedText;
                    let currentPos = 0;
                    
                    // Split text by **bold** markers
                    const boldRegex = /\*\*([^*]+)\*\*/g;
                    let match;
                    let lastIndex = 0;
                    
                    while ((match = boldRegex.exec(cleanedText)) !== null) {
                        // Add text before bold
                        if (match.index > lastIndex) {
                            segments.push({ text: cleanedText.substring(lastIndex, match.index), bold: false });
                        }
                        // Add bold text
                        segments.push({ text: match[1], bold: true });
                        lastIndex = match.index + match[0].length;
                    }
                    
                    // Add remaining text
                    if (lastIndex < cleanedText.length) {
                        segments.push({ text: cleanedText.substring(lastIndex), bold: false });
                    }
                    
                    // Render segments with mixed formatting
                    const lineHeight = fontSize * lineSpacing;
                    let currentLine = '';
                    let currentLineSegments = [];
                    
                    for (const segment of segments) {
                        const words = segment.text.split(' ');
                        
                        for (let i = 0; i < words.length; i++) {
                            const word = words[i] + (i < words.length - 1 ? ' ' : '');
                            const testLine = currentLine + word;
                            
                            pdf.setFont('helvetica', 'normal');
                            const testWidth = pdf.getTextWidth(testLine);
                            
                            if (testWidth > maxWidth && currentLine !== '') {
                                // Render current line
                                if (yPosition + lineHeight > pageHeight - margin) {
                                    pdf.addPage();
                                    yPosition = margin;
                                }
                                
                                let xPos = margin;
                                for (const lineSeg of currentLineSegments) {
                                    pdf.setFont('helvetica', lineSeg.bold ? 'bold' : 'normal');
                                    pdf.text(lineSeg.text, xPos, yPosition);
                                    xPos += pdf.getTextWidth(lineSeg.text);
                                }
                                
                                yPosition += lineHeight;
                                currentLine = word;
                                currentLineSegments = [{ text: word, bold: segment.bold }];
                            } else {
                                currentLine = testLine;
                                if (currentLineSegments.length > 0 && 
                                    currentLineSegments[currentLineSegments.length - 1].bold === segment.bold) {
                                    // Append to last segment if same style
                                    currentLineSegments[currentLineSegments.length - 1].text += word;
                                } else {
                                    currentLineSegments.push({ text: word, bold: segment.bold });
                                }
                            }
                        }
                    }
                    
                    // Render last line
                    if (currentLine !== '') {
                        if (yPosition + lineHeight > pageHeight - margin) {
                            pdf.addPage();
                            yPosition = margin;
                        }
                        
                        let xPos = margin;
                        for (const lineSeg of currentLineSegments) {
                            pdf.setFont('helvetica', lineSeg.bold ? 'bold' : 'normal');
                            pdf.text(lineSeg.text, xPos, yPosition);
                            xPos += pdf.getTextWidth(lineSeg.text);
                        }
                        
                        yPosition += lineHeight;
                    }
                } else {
                    // Simple text without inline formatting
                    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
                    const lines = pdf.splitTextToSize(cleanedText, maxWidth);
                    const lineHeight = fontSize * lineSpacing;

                    lines.forEach(line => {
                        if (yPosition + lineHeight > pageHeight - margin) {
                            pdf.addPage();
                            yPosition = margin;
                        }
                        pdf.text(line, margin, yPosition);
                        yPosition += lineHeight;
                    });
                }

                return yPosition;
            };

            // Helper function to add spacing
            const addSpacing = (space) => {
                yPosition += space;
                if (yPosition > pageHeight - margin) {
                    pdf.addPage();
                    yPosition = margin;
                }
            };

            // Add header with title
            pdf.setFillColor(0, 102, 204);
            pdf.rect(0, 0, pageWidth, 80, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(24);
            pdf.setFont('helvetica', 'bold');
            pdf.text('X Custom Analysis', margin, 50);
            yPosition = 100;

            // Add search name
            if (searchName) {
                pdf.setTextColor(100, 100, 100);
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'normal');
                pdf.text(`Search: ${searchName}`, margin, yPosition);
                yPosition += 30;
            }

            // Add timestamp
            pdf.setTextColor(100, 100, 100);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const timestamp = new Date().toLocaleString();
            pdf.text(`Generated: ${timestamp}`, margin, yPosition);
            yPosition += 30;

            // Process the markdown result
            const lines = result.split('\n');

            for (let i = 0; i < lines.length; i++) {
                let line = lines[i];

                // Skip empty lines but add spacing
                if (!line.trim()) {
                    addSpacing(8);
                    continue;
                }

                // Handle headers
                if (line.startsWith('####')) {
                    addSpacing(5);
                    addText(line.replace(/^####\s*/, ''), 11, true, [0, 102, 204]);
                    addSpacing(8);
                } else if (line.startsWith('###')) {
                    addSpacing(8);
                    addText(line.replace(/^###\s*/, ''), 13, true, [0, 102, 204]);
                    addSpacing(10);
                } else if (line.startsWith('##')) {
                    addSpacing(12);
                    addText(line.replace(/^##\s*/, ''), 15, true, [0, 102, 204]);
                    addSpacing(12);
                } else if (line.startsWith('#')) {
                    addSpacing(15);
                    addText(line.replace(/^#\s*/, ''), 18, true, [0, 102, 204]);
                    addSpacing(15);
                } else if (line.startsWith('- ') || line.startsWith('* ')) {
                    // Bullet points
                    const bulletText = line.replace(/^[-*]\s*/, '• ');
                    addText(bulletText, 11, false, [0, 0, 0], 1.5);
                } else if (/^\d+\.\s/.test(line)) {
                    // Numbered lists
                    addText(line, 11, false, [0, 0, 0], 1.5);
                } else if (line.startsWith('> ')) {
                    // Block quotes
                    const quoteText = line.replace(/^>\s*/, '');
                    addText(quoteText, 10, false, [100, 100, 100], 1.5);
                } else if (line.trim().startsWith('```')) {
                    // Code blocks - just skip the markers for now
                    continue;
                } else {
                    // Regular paragraph text
                    addText(line, 11, false, [0, 0, 0], 1.5);
                }
            }

            // Save the PDF
            const fileName = `X_Custom_Analysis_${searchName ? searchName.replace(/[^a-z0-9]/gi, '_') : 'Report'}_${new Date().getTime()}.pdf`;
            pdf.save(fileName);

            // Remove loading toast and show success
            loadingToast.remove();
            const successToast = document.createElement('div');
            successToast.className = 'alert alert-success position-fixed';
            successToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            successToast.innerHTML = '<i class="bi bi-check-circle me-2"></i>PDF downloaded successfully!';
            document.body.appendChild(successToast);
            setTimeout(() => successToast.remove(), 3000);
        } catch (err) {
            console.error('PDF export failed:', err);

            // Show error toast
            const errorToast = document.createElement('div');
            errorToast.className = 'alert alert-danger position-fixed';
            errorToast.style.cssText = 'top: 20px; right: 20px; z-index: 9999;';
            errorToast.innerHTML = '<i class="bi bi-exclamation-circle me-2"></i>PDF export failed. Please try again.';
            document.body.appendChild(errorToast);
            setTimeout(() => errorToast.remove(), 3000);
        }
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-xl" style={{ maxHeight: '90vh' }}>
                <div className="modal-content" style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="bi bi-magic me-2"></i>
                            Custom Analysis: {searchName}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleClose}
                            aria-label="Close"
                        ></button>
                    </div>

                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                        {status === 'configuring' && (
                            <div className="configuration-panel">
                                {/* Prompt Selection */}
                                <div className="mb-4">
                                    <h6 className="mb-3">
                                        <i className="bi bi-card-text me-2"></i>
                                        Analysis Prompt
                                    </h6>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Select Prompt Template:</label>
                                        <select
                                            className="form-select"
                                            value={selectedPromptId}
                                            onChange={(e) => handlePromptSelection(e.target.value)}
                                            disabled={promptsLoading}
                                        >
                                            <option value="">-- Select a prompt template or enter custom prompt below --</option>
                                            {clientTypePrompts.map(prompt => (
                                                <option key={prompt.prompt_id} value={prompt.prompt_id}>
                                                    {prompt.name || prompt.app_id}
                                                </option>
                                            ))}
                                        </select>
                                        {promptsLoading && (
                                            <div className="text-muted small mt-1">
                                                <i className="bi bi-hourglass-split me-1"></i>
                                                Loading prompts...
                                            </div>
                                        )}
                                    </div>

                                    {selectedPromptId ? (
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label">System Prompt:</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={4}
                                                    value={systemPrompt}
                                                    onChange={e => setSystemPrompt(e.target.value)}
                                                    placeholder="Enter system prompt..."
                                                    style={{ fontSize: '14px', lineHeight: '1.4' }}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">User Prompt:</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={6}
                                                    value={userPrompt}
                                                    onChange={e => setUserPrompt(e.target.value)}
                                                    placeholder="Enter prompt text..."
                                                    style={{ fontSize: '14px', lineHeight: '1.4' }}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <div className="mb-3">
                                            <label className="form-label">Custom Prompt:</label>
                                            <textarea
                                                className="form-control"
                                                rows={8}
                                                value={customPrompt}
                                                onChange={e => setCustomPrompt(e.target.value)}
                                                placeholder="Example: Analyze these tweets for sentiment trends, key topics, and influential voices..."
                                                style={{ fontSize: '14px', lineHeight: '1.4' }}
                                            />
                                        </div>
                                    )}

                                    <div className="text-muted small">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Your custom prompt will be used to analyze the selected results. The analysis will not be saved automatically.
                                    </div>
                                </div>

                                {/* Result Selection */}
                                <div className="mb-3">
                                    <h6 className="mb-3">
                                        <i className="bi bi-list-check me-2"></i>
                                        Select Results to Analyze ({selectedResultIds.length} selected)
                                    </h6>
                                    
                                    {availableResults.length === 0 ? (
                                        <div className="alert alert-info">
                                            <i className="bi bi-info-circle me-2"></i>
                                            No results available for this search
                                        </div>
                                    ) : (
                                        <div className="list-group" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {availableResults.map((result) => {
                                                const isSelected = selectedResultIds.includes(result.id);
                                                console.log('Result run_type:', result.run_type, 'for result:', result.id);
                                                return (
                                                    <div 
                                                        key={result.id} 
                                                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${isSelected ? 'active' : ''}`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => toggleResultSelection(result.id)}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <div className="form-check">
                                                                <input 
                                                                    className="form-check-input" 
                                                                    type="checkbox" 
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                />
                                                                <label className="form-check-label ms-2">
                                                                    {formatDate(result.executed_at)}
                                                                </label>
                                                            </div>
                                                            {result.run_type && (
                                                                <span 
                                                                    className={`badge ms-2`}
                                                                    style={{ 
                                                                        fontSize: '0.7rem',
                                                                        backgroundColor: result.run_type === 'scheduled' ? '#198754' : '#0dcaf0',
                                                                        color: 'white',
                                                                        opacity: isSelected ? '1' : '0.9'
                                                                    }}
                                                                >
                                                                    {result.run_type === 'scheduled' ? (
                                                                        <><i className="bi bi-clock-history me-1"></i>Scheduled</>
                                                                    ) : (
                                                                        <><i className="bi bi-hand-index me-1"></i>Manual</>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span 
                                                            className="badge rounded-pill"
                                                            style={{
                                                                backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : '#0d6efd',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            {result.tweet_count} tweets
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {selectedResultIds.length > 0 && (
                                        <div className="text-muted small mt-2">
                                            <i className="bi bi-check-circle me-1"></i>
                                            {selectedResultIds.length} result{selectedResultIds.length > 1 ? 's' : ''} will be combined for analysis
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="alert alert-danger mt-3">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {error}
                                    </div>
                                )}
                            </div>
                        )}

                        {(status === 'starting' || status === 'pending') && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="text-muted">Starting custom analysis...</p>
                            </div>
                        )}

                        {status === 'processing' && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Processing...</span>
                                </div>
                                <p className="text-muted">Analyzing {selectedResultIds.length} result{selectedResultIds.length > 1 ? 's' : ''} with custom prompt...</p>
                                <small className="text-muted">This may take a moment.</small>
                            </div>
                        )}

                        {status === 'completed' && result && (
                            <div className="analysis-result h-100">
                                <div
                                    className="analysis-text p-3 border rounded bg-light h-100"
                                    style={{
                                        overflowY: 'auto',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <MarkdownRenderer content={result} />
                                </div>
                            </div>
                        )}

                        {status === 'failed' && error && (
                            <div className="alert alert-danger">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <strong>Error:</strong> {error}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <div className="d-flex justify-content-between w-100 align-items-center">
                            <div className="d-flex gap-2">
                                {status === 'completed' && result && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={handleCopyToClipboard}
                                            title="Copy to clipboard"
                                        >
                                            <i className="bi bi-clipboard me-2"></i>
                                            Copy
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success"
                                            onClick={handleExportToPdf}
                                            title="Export to PDF"
                                        >
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                {status === 'configuring' && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={startCustomAnalysis}
                                        disabled={selectedResultIds.length === 0}
                                    >
                                        <i className="bi bi-play-fill me-2"></i>
                                        Start Analysis
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
